/**
 * <contact-popin> — dual-submit hook for the personal-shopper / special-order popin.
 *
 * The native {% form 'contact' %} submission keeps working as-is (store inbox
 * notification). In parallel, we populate a hidden {% form 'customer' %} with
 * the visitor's details plus a `lead:<lead_type>` tag and POST it via
 * sendBeacon, so Shopify creates/updates a Customer record. Shopify Flow can
 * then trigger on `Customer tag added` to send an auto-confirmation email.
 *
 * Wrapping the popin body in a <contact-popin> custom element gives us
 * reliable connect/disconnect lifecycle hooks — important because <x-modal>
 * moves the node into <body> (`append-to-body`), which would otherwise break
 * statically-scoped listeners.
 */

if (!customElements.get('contact-popin')) {
  class ContactPopin extends HTMLElement {
    static SELECTORS = {
      form: 'form.contact-popin__form',
      hiddenForm: 'form.contact-popin__customer-form',
      submitButton: '.contact-popin__submit-button',
      statusBanner: '.banner--success, .banner--error',
      modalHost: 'x-modal',
    };

    static FIELDS = {
      email: 'contact[email]',
      firstName: 'contact[first_name]',
      lastName: 'contact[last_name]',
      telephone: 'contact[telephone]',
      phone: 'contact[phone]',
      leadType: 'contact[lead_type]',
      tags: 'contact[tags]',
    };

    static TAG_PREFIX = 'lead:';

    static DATASET = { beaconSent: 'contactPopinBeaconSent' };

    static EVENTS = { variantChange: 'variant:change' };

    connectedCallback () {
      this._abort = new AbortController();
      this._form = this.querySelector(ContactPopin.SELECTORS.form);
      this._hiddenForm = this.querySelector(ContactPopin.SELECTORS.hiddenForm);

      if (!this._form || !this._hiddenForm) return;

      const { signal } = this._abort;

      // Primary: button click fires before hCaptcha intercepts the submit event
      // (hCaptcha calls form.submit() programmatically after its challenge,
      // which does NOT re-dispatch a submit event — so a submit-only listener
      // would miss some flows).
      this._form.addEventListener('click', (e) => this._onClick(e), { signal, capture: true });

      // Fallback: keyboard / programmatic submits that don't go through a click.
      this._form.addEventListener('submit', () => this._queueBeacon(), { signal, capture: true });

      document.addEventListener(ContactPopin.EVENTS.variantChange, (e) => this._onVariantChange(e), { signal });

      this._reopenIfFormPosted();
    }

    disconnectedCallback () {
      this._abort?.abort();
    }

    /**
     * When Shopify's contact form posts, the page reloads and the success/error
     * banner is rendered inside this popin's form — but the modal is closed by
     * default, so the visitor never sees the feedback. If a banner is present on
     * load, re-open the host modal so the message is visible.
     */
    _onVariantChange (e) {
      const variant = e.detail?.variant;
      if (!variant) return;

      const skuEl = this.querySelector('.contact-popin__product-sku');
      if (skuEl) skuEl.textContent = `Ref. ${variant.sku ?? ''} · ${variant.title ?? ''}`;

      const variantInput = this._form?.querySelector('[name="contact[current_variant]"]');
      if (variantInput) variantInput.value = variant.id;
    }

    _reopenIfFormPosted () {
      if (!this._form.querySelector(ContactPopin.SELECTORS.statusBanner)) return;
      const modal = this.closest(ContactPopin.SELECTORS.modalHost);
      if (!modal) return;
      requestAnimationFrame(() => modal.show?.({ animate: false }));
    }

    _onClick (e) {
      if (!e.target?.closest?.(ContactPopin.SELECTORS.submitButton)) return;
      this._queueBeacon();
    }

    _queueBeacon () {
      if (this._form.dataset[ContactPopin.DATASET.beaconSent] === 'true') return;

      const email = this._readField(this._form, ContactPopin.FIELDS.email);
      const leadType = this._readField(this._form, ContactPopin.FIELDS.leadType);
      if (!email || !leadType) return;

      this._populateHiddenForm(email, leadType);
      this._sendBeacon();
    }

    _populateHiddenForm (email, leadType) {
      const { FIELDS, TAG_PREFIX } = ContactPopin;
      this._setHiddenValue(FIELDS.email, email);
      this._setHiddenValue(FIELDS.firstName, this._readField(this._form, FIELDS.firstName));
      this._setHiddenValue(FIELDS.lastName, this._readField(this._form, FIELDS.lastName));
      this._setHiddenValue(FIELDS.phone, this._readField(this._form, FIELDS.telephone));
      this._setHiddenValue(FIELDS.tags, `${TAG_PREFIX}${leadType}`);
    }

    _sendBeacon () {
      try {
        navigator.sendBeacon(this._hiddenForm.action, new FormData(this._hiddenForm));
        this._form.dataset[ContactPopin.DATASET.beaconSent] = 'true';
      } catch (_) {
        /* primary contact form still delivers the internal notification */
      }
    }

    _setHiddenValue (name, value) {
      const el = this._hiddenForm.querySelector(`[name="${name}"]`);
      if (el) el.value = value || '';
    }

    _readField (form, name) {
      const el = form.querySelector(`[name="${name}"]`);
      return el?.value?.trim() || '';
    }
  }

  customElements.define('contact-popin', ContactPopin);
}
